import React, {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {StepIndex} from "modules/package/types/PackageForm";
import ButtonPrimary from "modules/ui/shared/Button/ButtonPrimary";
import StepIndicator from "./steps/StepIndicator";
import StepSelectPackageRoom from "./steps/StepSelectPackageRoom";
import StepSelectMoreRooms from "./steps/StepSelectMoreRooms";
import StepPersonalDetails from "./steps/StepPersonalDetails";
import StepRoomDetails from "./steps/StepRoomDetails";
import useTranslate from "../../../core/languages/hooks/useTranslate";
import BannerImage from "../../../common/components/BannerImage";
import {useParams} from "react-router-dom";
import usePackageData from "../../hooks/usePackageData";
import {getAccommodationDates} from "../../utils/getAccommodationDates";
import Loader from "../../../ui/shared/Loader/Loader";
import useProfileGet from "../../../profile/hooks/useProfileGet";
import PackageWizardAlert from "./PackageWizardAlert";
import StepTransferDetails from "./steps/StepTransferDetails";
import StepOtherTransferDetails from "./steps/StepOtherTransferDetails";
import {Alert} from "../../../ui/shared/Alert/Alert";
import {getStepFields} from "../../utils/getStepFieldsToValidate";
import {
  getOptionalTransferFieldsToUnregister,
  getOptionalTransferFieldsToValidate,
  hasAnyOptionalTransferValue,
} from "../../utils/optionalTransferStepValidation";
import {usePackageWizardSubmit} from "../../hooks/usePackageWizardSubmit";
import {TOTAL_STEPS} from "../../constants";
import {WizardSaveInput} from "../../types/WizardSaveInput";

const PackageWizard = () => {
  const [currentStep, setCurrentStep] = useState<StepIndex>(1);
  const t = useTranslate();
  const {preBookingInternalId} = useParams();
  const {preBooking, eventCity, city, hotelInfo, isLoading} = usePackageData(preBookingInternalId);

  const form = useForm<WizardSaveInput>({mode: "onSubmit", shouldFocusError: false});
  const {handleSubmit, setValue, trigger, watch, getValues, unregister} = form;
  const selectedPaymentCardUuid = watch('selectedPaymentCardUuid');
  const accommodationDates = getAccommodationDates(preBooking, eventCity);
  const {data: profile} = useProfileGet({execute: true});

  // Use the extracted hook for submit logic
  const {onSubmit, isSubmitting, transferError, setTransferError} = usePackageWizardSubmit({
    preBooking: preBooking!,
    eventCity: eventCity!,
    selectedPaymentCardUuid,
  });

  // set the eventCity and city values in the form when they are available
  useEffect(() => {
    if (eventCity) {
      setValue("eventCity", eventCity.id);
    }
    if (city) {
      setValue("city", city.id);
    }
  }, [eventCity, city, setValue]);

  // Show loader while data is loading
  if (!preBooking || !eventCity || !hotelInfo || isLoading) {
    return (
      <div className="nc-PackageWizard flex flex-col relative w-full">
        <div className="flex justify-center py-16">
          <Loader/>
        </div>
      </div>
    );
  }

  /**
   * Validate current step and move to next step
   */
  const handleNextStep = async (e?: React.MouseEvent) => {
    e?.preventDefault();

    // Solo permitir avanzar si estamos en un step anterior al 5
    if (currentStep < TOTAL_STEPS) {
      let fieldsToValidate = getStepFields(currentStep);

      // Step 5: optional transfer — all-or-nothing, owned only by the wizard (no transfers coupling)
      if (currentStep === 5) {
        const values = getValues();
        if (hasAnyOptionalTransferValue(values, values.direction)) {
          fieldsToValidate = getOptionalTransferFieldsToValidate(values, values.direction);
        } else {
          // Skip empty optional section: unregister so final submit does not hit required rules
          unregister(getOptionalTransferFieldsToUnregister(values.direction));
          fieldsToValidate = [];
        }
      }

      const isValid = fieldsToValidate.length === 0 || await trigger(fieldsToValidate);

      if (isValid) {
        setCurrentStep((currentStep + 1) as StepIndex);
        window.scrollTo({top: 0, behavior: "smooth"});
      }
    }
  };

  /**
   * Rollback to previous step
   */
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as StepIndex);
      window.scrollTo({top: 0, behavior: "smooth"});
    }
  };

  /**
   * Maneja el envío final del formulario
   */
  const handleSubmitForm = async (data: WizardSaveInput) => {
    await onSubmit(data);
  };

  return (
    <div className="nc-PackageWizard flex flex-col relative w-full">
      {/* Banner Image Component */}
      {eventCity?.bannerContentUrl && (
        <BannerImage mainImage={eventCity?.bannerContentUrl || ""}/>
      )}
      {/* Form container */}
      <main className="container mt-11 mb-24 lg:mb-32 flex justify-center w-full">
        <div className="w-full lg:w-5/5 xl:w-3/3">
          {/* Form Container with negative margin overlap */}
          <div className="flex justify-center mb-12 -mt-6 lg:-mt-20 w-full px-4">
            <div
              className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 px-0 sm:p-6 xl:p-8 bg-white dark:bg-neutral-900">
              <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleSubmitForm)}>
                  {/* Success Banner */}
                  <PackageWizardAlert eventCity={eventCity} accommodationDates={accommodationDates}/>

                  {/* Transfer Error Alert */}
                  {transferError && (
                    <Alert
                      type="error"
                      closable={true}
                      onClose={() => setTransferError(null)}
                      containerClassName="mb-5"
                    >
                      {t("transfers.submissionFailed") || "Error: " + transferError}
                    </Alert>
                  )}

                  {/* Indicador de pasos */}
                  <StepIndicator currentStep={currentStep}/>
                  {/* Contenido del paso actual */}
                  <div className="min-h-96">
                    {currentStep === 1 &&
                      <StepSelectPackageRoom preBooking={preBooking} accommodationDates={accommodationDates} eventCity={eventCity}/>
                    }
                    {currentStep === 2 &&
                      <StepSelectMoreRooms
                        preBooking={preBooking}
                        eventCity={eventCity}
                        currencySymbol={hotelInfo.currencySymbol}
                      />
                    }
                    {currentStep === 3 &&
                      <StepRoomDetails preBooking={preBooking} hotelInfo={hotelInfo}/>
                    }
                    {currentStep === 4 &&
                      <StepTransferDetails eventCity={eventCity} hotelInfo={hotelInfo}/>
                    }
                    {currentStep === 5 &&
                      <StepOtherTransferDetails eventCity={eventCity}/>
                    }
                    {currentStep === 6 &&
                      <StepPersonalDetails hotelInfo={hotelInfo} profile={profile} preBooking={preBooking}/>
                    }
                  </div>

                  {/* Navegación entre pasos */}
                  <div className="flex justify-between pt-6 border-t border-neutral-200 dark:border-neutral-700">
                    <button
                      type="button"
                      onClick={handlePreviousStep}
                      disabled={currentStep === 1}
                      className={`px-6 py-2 rounded-lg font-medium transition ${
                        currentStep === 1
                          ? "bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed"
                          : "bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-white hover:bg-neutral-300 dark:hover:bg-neutral-600"
                      }`}
                    >
                      {t("common.actions.previous")}
                    </button>

                    {currentStep < TOTAL_STEPS ? (
                      <ButtonPrimary type="button" onClick={handleNextStep} disabled={isSubmitting}>
                        {t("common.actions.next")}
                      </ButtonPrimary>
                    ) : (
                      <ButtonPrimary type="submit" disabled={isSubmitting}
                                     className={isSubmitting ? "opacity-70 cursor-not-allowed" : ""}>
                        {isSubmitting ? t("common.actions.sending") : t("common.actions.complete")}
                      </ButtonPrimary>
                    )}
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PackageWizard;
